import React, { useEffect, useState } from 'react'
import { ToolTipUI } from '../../../widgets/components/UI/ToolTipUI'
import { useIntl } from 'react-intl'
import { Formik, Form, Field } from 'formik'
import { InputPeriod } from '../../../widgets/components/Input/InputPeriod'
import { periodOptions } from '../../../../core/_constants'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { useAuth } from '../../../auth'
import { useParams } from 'react-router-dom'
import { getCompilanceLegal, updateSecurityCompilance } from '../../core/_requests'
import { formatDateString } from '../../../../core/_util'
import { addDays, addMonths, addYears, format } from 'date-fns'
import Skeleton from 'react-loading-skeleton'

const ComplianceAndLegal = () => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const { recordId } = useParams()
  const [loading, setLoading] = useState<boolean>(false)
  const [compilanceDataLoading, setCompilanceDataLoading] = useState<boolean>(false)
  const [compilanceData, setCompilanceData] = useState<any>()
  const [archivalDate, setArchivalDate] = useState<any>()
  const [updatedLegalPeriodEndDate, setUpdatedLegalPeriodEndDate] = useState<string>('')
  const [legalPeriod, setLegalPeriod] = useState<string>('')

  const initialValues = {
    isLegalHold: compilanceData?.legalHold || false,
    legalHoldExpiryPeriod: compilanceData?.legalPeriod?.split(' ')[1] || null,
    legalHoldExpiryValue: compilanceData?.legalPeriod?.split(' ')[0] || null,
  }

  const userSchema = Yup.object().shape({
    isLegalHold: Yup.boolean(),
    legalHoldExpiryPeriod: Yup.string()
      .nullable()
      .when('isLegalHold', {
        is: true,
        then: (schema) => schema.required('LegalHoldExpiryPeriod is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    legalHoldExpiryValue: Yup.number()
      .nullable()
      .when(['isLegalHold', 'legalHoldExpiryPeriod'], {
        is: (isLegalHold: boolean, legalHoldExpiryPeriod: string) =>
          isLegalHold && ['Days', 'Years', 'Months'].includes(legalHoldExpiryPeriod),
        then: (schema) =>
          schema
            .min(0, 'LegalHoldValue cannot be negative')
            .typeError('LegalHoldExpiryValue must be a number')
            .required('LegalHoldExpiryValue is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  })

  const calculateLegalPeriodEndDate = (archivalDate?: string, legalPeriod?: string) => {
    if (!archivalDate || !legalPeriod) return '-'

    const archival = new Date(archivalDate)
    if (isNaN(archival.getTime())) return '-'

    const [value, unit] = legalPeriod.split(' ')
    const periodValue = parseInt(value, 10)

    if (isNaN(periodValue)) return '-'

    let calculatedDate: Date
    switch (unit.toLowerCase()) {
      case 'days':
        calculatedDate = addDays(archival, periodValue)
        break
      case 'months':
        calculatedDate = addMonths(archival, periodValue)
        break
      case 'years':
        calculatedDate = addYears(archival, periodValue)
        break
      default:
        return 'N/A'
    }

    const formattedDate = format(calculatedDate, 'dd MMM, yyyy')

    setUpdatedLegalPeriodEndDate(formattedDate)
  }

  const handleSubmit = async (values: any) => {
    if (!companyId || !recordId) return
    setLoading(true)
    try {
      const payload = {
        legalHold: values?.isLegalHold,
        legalPeriod:
          values?.legalHoldExpiryValue && values?.legalHoldExpiryPeriod
            ? `${values.legalHoldExpiryValue} ${values.legalHoldExpiryPeriod}`
            : null,
      }
      const {
        data: { data, success, errors },
      } = await updateSecurityCompilance(companyId, recordId, payload)
      if (success) {
        toast.success('Legal and compilance updated successfully')
        calculateLegalPeriodEndDate(archivalDate, data?.legalPeriod)
        setLegalPeriod(data?.legalPeriod)
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const getAllCompilanceLegal = async () => {
    if (!companyId || !recordId) return
    setCompilanceDataLoading(true)
    try {
      const {
        data: { data, success, errors },
      } = await getCompilanceLegal(companyId, recordId)
      if (success) {
        setCompilanceData(data?.getDetails)
        setArchivalDate(data?.archivalDate)
        setLegalPeriod(data?.getDetails?.legalPeriod)
        calculateLegalPeriodEndDate(data?.archivalDate, data?.getDetails?.legalPeriod)
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
      console.log(err)
    } finally {
      setCompilanceDataLoading(false)
    }
  }

  useEffect(() => {
    getAllCompilanceLegal()
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className='bg-white p-4 rounded'>
      <h1 className='font-size-16'>{formatMessage({ id: 'Compliance & Legal' })}</h1>
      {archivalDate && <div className='my-8'>
        <p className='text-muted font-size-13 m-0'>
          {formatMessage({ id: 'Original Archival Date' })}
          <span>
            <ToolTipUI tooltipText='The original date on which this document will be archived based on retention rules. The below legal hold option can extend this time period further.' />
          </span>
        </p>
        {compilanceDataLoading ? (
          <Skeleton
            height={30}
            width={80}
            baseColor='var(--kt-skeleton-bg-color)'
            highlightColor='var(--kt-skeleton-highlight-bg-color)'
          />
        ) : (
          <p className='font-size-13'>{formatDateString(archivalDate)}</p>
        )}
      </div>}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={userSchema}
        enableReinitialize={true}
        validateOnMount
      >
        {(formik) => {
          return (
            <Form>
              <div className='form-check form-check-custom form-check-solid form-check-primary form-switch'>
                <label className='togglebtn mx-0'>
                  <Field
                    className='togglebtn__input'
                    name='isLegalHold'
                    type='checkbox'
                    formik={formik}
                    onChange={(event: any) => {
                      formik.handleChange(event)
                      const isChecked = event.target.checked

                      if (!isChecked) {
                        formik.setFieldValue('legalHoldExpiryValue', '')
                        formik.setFieldValue('legalHoldExpiryPeriod', null)
                        formik.submitForm()
                      }
                    }}
                  />
                  <div className='togglebtn__fill'></div>
                </label>
                <div className='d-flex flex-column justify-content-end mt-2'>
                  <div className='d-flex'>
                    <div>
                      <label
                        className='form-check-label fw-semibold font-size-13 title-notification'
                        data-bs-toggle='tooltip'
                      >
                        {formatMessage({ id: 'Legal Hold' })}
                      </label>
                    </div>
                  </div>
                  <label
                    className='form-check-label text-clr58 fw-normal fs-7'
                    data-bs-toggle='tooltip'
                  >
                    {formatMessage({
                      id: 'Turn this “On” to mark this record as "On Legal Hold" to prevent them from being modified or deleted',
                    })}
                  </label>
                </div>
              </div>
              <div className='w-md-50 w-100 my-4'>
                <InputPeriod
                  label={formatMessage({
                    id: 'Legal Hold Expiry Period',
                  })}
                  toolTipText={formatMessage({
                    id: 'The duration for which this document will remain on legal hold. This is in addition to any existing legal hold period, if applicable.',
                  })}
                  fieldName='legalHoldExpiryValue'
                  placeholder={formatMessage({ id: 'Enter duration' })}
                  placeholderSelect='Enter duration'
                  formik={formik}
                  options={periodOptions}
                  isTooltipForModal={true}
                  selectOptionFieldName='legalHoldExpiryPeriod'
                  disabled={!formik.values.isLegalHold}
                />
              </div>
              {formik?.values?.isLegalHold && archivalDate && legalPeriod && (
                <div>
                  <p className='font-size-13 text-muted fw-semibold'>
                    {formatMessage({ id: 'Document will now be archived on: ' })}
                    {compilanceDataLoading ? (
                      <Skeleton
                        height={30}
                        width={80}
                        baseColor='var(--kt-skeleton-bg-color)'
                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                      />
                    ) : (
                      <span className='text-primary'>{updatedLegalPeriodEndDate}</span>
                    )}
                  </p>
                </div>
              )}

              <div className='d-flex justify-content-end'>
                <button
                  type='submit'
                  className='btn bg-primary text-white custom-button-property border border-1 mb-md-4 mb-0 px-6 py-4 text-center w-100 w-md-auto order-1 order-md-2'
                  disabled={(!formik.isValid && formik.values.isLegalHold) || loading}
                >
                  {!loading && (
                    <span className='indicator-label font-size-13 p-0'>
                      {formatMessage({ id: 'Save Changes' })}
                    </span>
                  )}
                  {loading && (
                    <span className='indicator-label font-size-13 p-0'>
                      {formatMessage({ id: 'Please wait...' })}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
export default ComplianceAndLegal
